import React, { useState, useEffect, Fragment, useCallback, useRef } from "react"
import { useHistory, useParams, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Tab, Menu, Transition } from "@headlessui/react"
import { HiChevronDown } from "react-icons/hi"

import Header from "../../components/header"
import ConfirmationModal from "../../components/confirmation"
import { twClassNames } from "../../helpers/classNames"
import { useDestinations } from "../../hooks/useDestinations"
import type { ConfirmationState } from "../../constants/types"
import { useActivities } from "../../hooks/useActivities"
import { TabActivities, TabHandbooks } from "./components"

type DropdownButtonProps = {
  title: string
  children: React.ReactNode
}

const DropdownButton: React.FC<DropdownButtonProps> = ({ title, children }) => (
  <Menu as="div" className="tw-relative tw-inline-block tw-text-left">
    <Menu.Button className="tw-btn tw-btn-primary tw-min-w-[180px]">
      {title}
      <HiChevronDown className="tw-absolute tw-right-0 tw-mr-4 tw-text-white" aria-hidden="true" />
    </Menu.Button>
    <Transition
      as={Fragment}
      enter="tw-transition tw-ease-out tw-duration-100"
      enterFrom="tw-transform tw-opacity-0 tw-scale-95"
      enterTo="tw-transform tw-opacity-100 tw-scale-100"
      leave="tw-transition tw-ease-in tw-duration-75"
      leaveFrom="tw-transform tw-opacity-100 tw-scale-100"
      leaveTo="tw-transform tw-opacity-0 tw-scale-95">
      <Menu.Items
        className="tw-absolute tw-right-0 tw-z-10 tw-mt-2 tw-w-56 tw-origin-top-right tw-rounded-md tw-bg-white
          tw-shadow-lg tw-border tw-border-dark-15 focus:tw-outline-none">
        <div className="tw-py-1">{children}</div>
      </Menu.Items>
    </Transition>
  </Menu>
)

type RouteParams = {
  active_tab?: "activities" | "handbooks"
}

const CONFIRMATION_DEFAULT = {
  show: false,
  callback: null,
  danger: false,
  title: "",
  content: ""
}

const SEARCH_DEBOUNCE_MS = 500

const ProjectsContainer: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const [confirmation, setConfirmation] = useState<ConfirmationState>(CONFIRMATION_DEFAULT)
  const [query, setQuery] = useState("")
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [destinationsPage, setDestinationsPage] = useState(1)
  const [activitiesPage, setActivitiesPage] = useState(1)
  const [maxRows, setMaxRows] = useState(() => Number(localStorage.getItem("display.max_rows")) || 5)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const { active_tab } = useParams<RouteParams>()

  const {
    data: destinationsData,
    isLoading: isLoadingDestinations,
    refetch: refetchDestinations
  } = useDestinations({
    page: destinationsPage,
    limit: maxRows,
    search: debouncedQuery
  })
  const destinations = destinationsData?.data ?? []
  const destinationsPagination = destinationsData?.pagination ?? { page: 1, limit: 50, total: 0, totalPages: 0 }

  const {
    data: activitiesData,
    isLoading: isLoadingActivities,
    refetch: refetchActivities
  } = useActivities({
    page: activitiesPage,
    limit: maxRows,
    search: debouncedQuery
  })
  const activities = activitiesData?.data ?? []
  const activitiesPagination = activitiesData?.pagination ?? { page: 1, limit: 50, total: 0, totalPages: 0 }

  const isLoading = isLoadingDestinations || isLoadingActivities

  const fetchContent = useCallback(() => {
    setDestinationsPage(1)
    setActivitiesPage(1)
    setDebouncedQuery(query)
    refetchDestinations()
    refetchActivities()
  }, [query, refetchDestinations, refetchActivities])

  // Debounced search: reset to page 1 when query changes
  useEffect(() => {
    clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setDestinationsPage(1)
      setActivitiesPage(1)
      setDebouncedQuery(query)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(searchTimerRef.current)
  }, [query])

  useEffect(() => {
    localStorage.setItem("display.max_rows", maxRows.toString())
  }, [maxRows])

  const handleDestinationsPageChange = (page: number) => {
    setDestinationsPage(page)
  }

  const handleActivitiesPageChange = (page: number) => {
    setActivitiesPage(page)
  }

  const handleMaxRowsChange = (rows: number) => {
    setMaxRows(rows)
    setDestinationsPage(1)
    setActivitiesPage(1)
  }

  return (
    <>
      <ConfirmationModal
        show={confirmation.show}
        title={confirmation.title}
        content={confirmation.content}
        danger={confirmation.danger}
        onOK={confirmation.callback}
        onCancel={() => setConfirmation(CONFIRMATION_DEFAULT)}
      />
      <Header />
      <div className="xl:tw-container tw-mx-auto tw-w-full tw-mt-24 tw-px-4 sm:tw-px-12">
        <h1 className="tw-font-display tw-font-semibold tw-text-4xl tw-mb-6">{t("projects.title")}</h1>
        <div className="tw-mb-12 tw-w-full">
          <Tab.Group
            selectedIndex={active_tab === "activities" ? 1 : 0}
            onChange={index => history.push(index === 0 ? "/handbooks" : "/activities")}>
            <div className="tw-shadow tw-bg-white tw-rounded">
              {/* Header group */}
              <div className="tw-flex tw-p-6 tw-pb-2 tw-flex-col-reverse md:tw-flex-row">
                {/* Search bar */}
                <div className="tw-grow md:tw-mr-6 max-md:tw-mt-4">
                  <div
                    className="tw-relative tw-w-full tw-rounded-lg tw-bg-white tw-text-left tw-cursor-default tw-h-10
                      tw-border tw-border-dark-15 focus:tw-outline-none">
                    <input
                      id="search"
                      type="text"
                      onChange={event => setQuery(event.target.value)}
                      className="tw-w-full tw-border-none tw-py-2 tw-pl-3 tw-pr-10 tw-leading-5 tw-rounded-lg
                        sm:tw-text-sm focus:tw-outline-primary-50"
                      placeholder={t("common.search.placeholder")}
                      spellCheck="false"
                    />
                  </div>
                </div>
                {/* Button group */}
                <div className="tw-flex tw-justify-center md:tw-justify-end tw-content-end tw-space-x-4">
                  <button
                    className="tw-btn tw-btn-outline tw-min-w-[180px]"
                    disabled={isLoading}
                    onClick={fetchContent}>
                    <span>{t("projects.button.refresh")}</span>
                  </button>
                  <DropdownButton title={t("projects.button.create.title")}>
                    <Menu.Item>
                      <Link
                        to="/handbook/create"
                        className="tw-block tw-px-4 tw-py-2 hover:tw-bg-gray hover:tw-no-underline hover:tw-text-dark">
                        <span>{t("projects.button.create.handbook")}</span>
                      </Link>
                    </Menu.Item>
                    <Menu.Item>
                      <Link
                        to="/activities/create"
                        className="tw-block tw-px-4 tw-py-2 hover:tw-bg-gray hover:tw-no-underline hover:tw-text-dark">
                        <span>{t("projects.button.create.activity")}</span>
                      </Link>
                    </Menu.Item>
                  </DropdownButton>
                </div>
              </div>
              {/* Tabs */}
              <Tab.List className="tw-border-b-2 tw-border-gray tw-mb-6">
                <ul className="tw-flex tw-flex-wrap -tw-mb-px tw-mx-6 tw-space-x-2">
                  <Tab as={Fragment}>
                    {({ selected }) => (
                      <li
                        className={twClassNames(
                          "tw-inline-block tw-text-dark-50 tw-cursor-pointer tw-py-4 tw-px-12 tw-text-center",
                          "hover:tw-text-dark-75 hover:tw-border-dark-25 tw-font-semibold",
                          "tw-border-transparent tw-border-b-2 focus:tw-outline-none",
                          selected && "!tw-text-dark !tw-border-primary"
                        )}>
                        {t("projects.handbooks", { length: destinationsPagination.total })}
                      </li>
                    )}
                  </Tab>
                  <Tab as={Fragment}>
                    {({ selected }) => (
                      <li
                        className={twClassNames(
                          "tw-inline-block tw-text-dark-50 tw-cursor-pointer tw-py-4 tw-px-12 tw-text-center",
                          "hover:tw-text-dark-75 hover:tw-border-dark-25 tw-font-semibold",
                          "tw-border-transparent tw-border-b-2 focus:tw-outline-none",
                          selected && "!tw-text-dark !tw-border-primary"
                        )}>
                        {t("projects.activities", { length: activitiesPagination.total })}
                      </li>
                    )}
                  </Tab>
                </ul>
              </Tab.List>
              {/* Content */}
              <div className="tw-px-6">
                <Tab.Panels className="tw-w-full tw-min-h-24">
                  {/* Handbooks */}
                  <TabHandbooks
                    destinations={destinations ?? []}
                    query={query}
                    pagination={destinationsPagination}
                    page={destinationsPage}
                    maxRows={maxRows}
                    onPageChange={handleDestinationsPageChange}
                    onMaxRowSelect={handleMaxRowsChange}
                    setConfirmation={setConfirmation}
                    hideConfirmation={() => setConfirmation(CONFIRMATION_DEFAULT)}
                    isLoading={isLoadingDestinations}
                  />
                  {/* Activities */}
                  <TabActivities
                    activities={activities ?? []}
                    query={query}
                    pagination={activitiesPagination}
                    page={activitiesPage}
                    maxRows={maxRows}
                    onPageChange={handleActivitiesPageChange}
                    onMaxRowSelect={handleMaxRowsChange}
                    setConfirmation={setConfirmation}
                    hideConfirmation={() => setConfirmation(CONFIRMATION_DEFAULT)}
                    isLoading={isLoadingActivities}
                  />
                </Tab.Panels>
              </div>
            </div>
          </Tab.Group>
        </div>
      </div>
    </>
  )
}

export default ProjectsContainer
